import { format } from 'date-fns'
import { ManAuthRequestResponse } from '../../types/ManAuthRequests'
import { DayHours } from '../../types/manAuthUpdateRequest'

export const calculateMaxAllowedHours = (request: ManAuthRequestResponse): number => {
  if (!request || !request.days || !request.absenceDays) return 0
  const daysWithPossibleManAuth = request.days.map(day => day.date)
  // Find the absence days that are covered by the man auth request.
  // Only these days are relevant for the calculation of max allowed hours.
  const relevantAbsenceDays = request.absenceDays.filter(day => daysWithPossibleManAuth.includes(day.absenceDate))
  const totalAbsenceHours = relevantAbsenceDays.reduce((sum, day) => sum + day.absentHours, 0)

  const ospAvailableHours = relevantAbsenceDays
    .filter(day => day.sickHoursToBePaid)
    .reduce((sum, day) => sum + day.sickHoursToBePaid!, 0)

  return totalAbsenceHours - ospAvailableHours
}

export const calculateReducedHours = (days: DayHours[], hoursDifference: number): DayHours[] => {
  if (hoursDifference <= 0) return days

  const result = [...days]
  let remainingDifference = hoursDifference

  for (let i = result.length - 1; i >= 0; i -= 1) {
    if (remainingDifference <= 0) break

    const reduction = Math.min(result[i].Hours, remainingDifference)

    result[i] = {
      ...result[i],
      Hours: result[i].Hours - reduction,
    }

    remainingDifference -= reduction
  }

  return result
}

export const calculateAddedHours = (
  days: DayHours[],
  hoursToAdd: number,
  defaultShiftLength: number,
  manAuthRequest: ManAuthRequestResponse
): DayHours[] => {
  const result = [...days]
  let remainingToAdd = hoursToAdd

  // First, try to add hours to existing days
  for (let i = 0; i < result.length; i += 1) {
    if (remainingToAdd <= 0) break

    const currentHours = result[i].Hours
    const availableCapacity = defaultShiftLength - currentHours

    if (availableCapacity > 0) {
      const addition = Math.min(availableCapacity, remainingToAdd)

      result[i] = {
        ...result[i],
        Hours: currentHours + addition,
      }

      remainingToAdd -= addition
    }
  }

  // If there are still hours to add, create new days
  if (remainingToAdd > 0) {
    // Get dates that are already being used (normalize to date-only format)
    const usedDates = new Set(result.map(day => format(new Date(day.Date), 'yyyy-MM-dd')))

    // Get available dates from absenceDays that aren't already used
    const availableDates = manAuthRequest.absenceDays
      .map(absenceDay => {
        const normalizedAbsenceDate = format(new Date(absenceDay.absenceDate), 'yyyy-MM-dd')
        return {
          original: absenceDay.absenceDate,
          normalized: normalizedAbsenceDate,
        }
      })
      .filter(dateObj => !usedDates.has(dateObj.normalized))
      .sort((a, b) => new Date(a.original).getTime() - new Date(b.original).getTime())
      .map(dateObj => dateObj.original)

    let dateIndex = 0
    while (remainingToAdd > 0 && dateIndex < availableDates.length) {
      const hoursForNewDay = Math.min(remainingToAdd, defaultShiftLength)

      result.push({
        Date: availableDates[dateIndex],
        Hours: hoursForNewDay,
      })

      remainingToAdd -= hoursForNewDay
      dateIndex += 1
    }

    if (remainingToAdd > 0) {
      console.warn(`Could not distribute ${remainingToAdd} hours as no more dates are available`)
    }
  }

  return result
}

export const calculateUpdatedDays = (
  hoursDifference: number,
  manAuthRequest: ManAuthRequestResponse,
  defaultShiftLength: number
): DayHours[] => {
  if (!manAuthRequest) return []

  const daysArray = [...manAuthRequest.days].map(day => ({
    Date: day.date,
    Hours: day.hours,
  }))

  daysArray.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime())

  if (hoursDifference > 0) {
    return calculateReducedHours(daysArray, hoursDifference)
  }

  if (hoursDifference < 0) {
    return calculateAddedHours(
      daysArray,
      Math.abs(hoursDifference),
      defaultShiftLength,
      manAuthRequest
    )
  }

  return daysArray
}
